import type { ThgAnnualSummary, ThgStationRow } from "../../../types/thgAnnualSummary";

/**
 * CLR-296: a station of an internal owner (cocharge / HadSol) is never paid. Stored v2 years report
 * `internal=false` until they are recomputed, but already carry payout state INTERNAL, so both are checked.
 */
export const isInternalRow = (row: Pick<ThgStationRow, "internal" | "payout">): boolean =>
  row.internal === true || row.payout?.state === "INTERNAL";

/**
 * An internal row that is really unpaid: its payout state is neither FINAL nor DRAFT (a cancelled earlier
 * note may still be attached). Only these are hidden from payout UI and exports.
 *
 * The backend lets an active DRAFT/FINAL credit note win over INTERNAL, and a legally issued document must
 * stay visible, openable and in the export.
 */
export const isUnpaidInternal = (row: Pick<ThgStationRow, "internal" | "payout">): boolean =>
  isInternalRow(row) && row.payout?.state !== "FINAL" && row.payout?.state !== "DRAFT";

/**
 * The rows an export contains and the totals that match them. The backend totals include internal
 * stations' paid kWh (and, for stored v2 FINAL years, their old payouts), so an export that drops those
 * rows must not print them. Summed in integer milli-kWh and cents so no float drift reaches the file.
 */
export const exportedRows = (summary: ThgAnnualSummary) => {
  const all = summary.stations ?? [];
  const stations = all.filter((station) => !isUnpaidInternal(station));
  const milliKwh = stations.reduce((sum, s) => sum + Math.round(s.paidEnergyKwh * 1000), 0);
  const cents = stations.reduce((sum, s) => sum + Math.round(s.payoutEur * 100), 0);
  return {
    stations,
    excluded: all.length - stations.length,
    paidEnergyKwh: milliKwh / 1000,
    payoutEur: cents / 100,
  };
};
